import {
  authMiddlewareClient,
  updateLoadClarity,
  translationClientMiddleware,
} from "@App/appReducer";
import { getWhitelabelDataSuccess, whitelabelMiddlewareClient } from "@App/whiteLabelReducer";
import { setLocalStorageData } from "@Components/LeadFormModal/data/leadFormReducer";
import config from "@Config/index";
import { AppState } from "@Store/index";
import isInternalUser from "@Utils/isInternalUser";

import wrapperHOC from "@Utils/wrapperHOC";
import checkValidWhitelabelSubdomain from "helpers/checkValidWhitelabelSubdomain";
import AmberLocalStorage from "@Utils/AmberlocalStorage";
import { getFontColorForBackground } from "helpers/colorHelper";
import { buildGoogleFontUrls } from "helpers/fontHelper";
import getSubdomain from "helpers/getSubdomain";
import useTrackerJS from "hooks/useTrackerJs";
import useWhitelabel from "hooks/useWhitelabel";
import { memo, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { matchRoutes } from "react-router-config";
import { useLocation } from "react-router";
import { onINP, onLCP, onTTFB, onCLS } from "web-vitals/attribution";
import sendAccessToken from "@Components/Login/helpers/sendAccessToken";
import {
  addBottomPaddingtoMessagingWindow,
  hideDefaultLauncherButtonIframe,
  hideUnreadMessagesIframe,
} from "@Pages/PropertyPage/data/helper";
import setCookie from "helpers/setCookie";
import useIsApp from "hooks/useIsApp";
import AppLayout from "./AppLayout";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import { useIframeDetection } from "hooks/useIsIframe";
import getDeviceType from "@Utils/getDeviceType";

const SnapchatPixelScript = memo(({ isUserLoggedIn, userData, nonce = "" }: any) => {
  let userEmail = "";
  let userPhone = "";
  let firstName = "";
  let lastName = "";

  if (isUserLoggedIn) {
    userEmail = userData?.email;
    userPhone = userData?.phone_details?.international;

    const nameParts = userData?.name?.split(" ");

    if (nameParts?.length > 0) {
      // eslint-disable-next-line prefer-destructuring
      firstName = nameParts[0];
      lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
    }
  }

  return (
    <Helmet>
      <script type="text/partytown" nonce={nonce}>
        {`
          (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
          {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
          a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
          r.src=n;var u=t.getElementsByTagName(s)[0];
          u.parentNode.insertBefore(r,u);})(window,document,
          '/proxy/https://sc-static.net/scevent.min.js');
          
          snaptr('init', '662bcf3e-ddc1-4b2c-88fc-9ba6cb341f17', {
          'user_email': '${userEmail}',
          'user_phone_number': '${userPhone}',
          'firstname': '${firstName}',
          'lastname': '${lastName}'
          });
          
          snaptr('track', 'PAGE_VIEW');
        `}
      </script>
    </Helmet>
  );
});

const ClarityScript = ({ nonce = "" }) => (
  <Helmet>
    {/* Move to partytown once fixed https://github.com/microsoft/clarity/issues/645 */}
    <script type="text/javascript" nonce={nonce}>
      {`
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${config.CLARITY_TOKEN}");
      `}
    </script>
  </Helmet>
);

const UserDataLayout = (props: any) => {
  const { routes } = props;
  const location = useLocation(); // Changed from useHistory to useLocation
  const {
    isAuthTokenPresent,
    isUserDataFetched,
    isUserLoggedIn,
    userData,
    isDesktop,
    loadClarity,
    isWhitelabelDataFetched,
    country,
    moduleName,
    userLanguage,
    loadedRouteTags, // Fixed: was loadedTolgeeTags
    isUsingS3Fallback,
    countryCode,
  } = useSelector(
    ({ appData, whitelabelData, searchPageData, propertyPageData }: AppState) => ({
      isAuthTokenPresent: appData.isAuthTokenPresent,
      isUserDataFetched: appData.isUserDataFetched,
      isUserLoggedIn: appData.isUserLoggedIn,
      userData: appData.userData,
      isDesktop: appData.isDesktop,
      loadClarity: appData.loadClarity,
      isWhitelabelDataFetched: whitelabelData.isWhitelabelDataFetched,
      country: appData.country,
      moduleName: appData.moduleName,
      userLanguage: appData.userLanguage,
      loadedRouteTags: appData.loadedRouteTags || [], // Fixed: was loadedTolgeeTags
      isUsingS3Fallback: appData.isUsingS3Fallback,
      countryCode:
        searchPageData?.region?.location?.country?.short_name ||
        propertyPageData?.data?.location?.country?.short_name,
    }),
    shallowEqual,
  );
  const isAmberApp = useIsApp();
  const { isWhitelabel } = useWhitelabel();
  const isMiniProgram = useIsMiniProgram();
  useTrackerJS();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source") || params.get("partner_source");
    const campaign = params.get("utm_campaign");
    if (!utmSource || campaign !== "partner") return;

    // Store the utm_source from the link directly in localStorage
    AmberLocalStorage.setItem("UTM_SOURCE", utmSource);
  }, []);
  const dispatch = useDispatch();
  useIframeDetection();

  // Initialize Redux dispatch globally for translation tracking
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).__reduxDispatch = dispatch;
    }
  }, [dispatch]);

  // Check if current page is a blog or news page based on moduleName
  const isContentPage = useMemo(
    () => moduleName?.startsWith("blog_") || moduleName?.startsWith("news_") || false,
    [moduleName],
  );

  const tolgeeRouteData = useMemo(
    () =>
      matchRoutes(routes, location.pathname).reduce(
        (res: any, { route, match }: any) => (match.isExact ? { route, match } : res),
        null,
      ),
    [location.pathname, routes],
  );

  useEffect(() => {
    if (isAuthTokenPresent && !isUserDataFetched) {
      dispatch(authMiddlewareClient());
    }
    dispatch(setLocalStorageData());

    const { hostname, subdomain, port, domainUrl } = getSubdomain(window.location);
    const urlParams = new URLSearchParams(window.location.search);

    if (config.IS_WHITELABEL_APP) {
      setCookie({ cname: "is_whitelabel", cvalue: "true", exdays: 365, secure: true });
    }

    if (typeof window !== "undefined" && config.IS_CHINA_APP && !isMiniProgram) {
      const hm = document.createElement("script");
      hm.src = "https://hm.baidu.com/hm.js?bc414516adf7927fdf3cc86278af6d68";

      const scripts = document.getElementsByTagName("script");
      if (scripts.length > 0 && scripts[0].parentNode) {
        scripts[0].parentNode.insertBefore(hm, scripts[0]);
      } else {
        // fallback: append to head or body if no <script> found
        (document.head || document.body).appendChild(hm);
      }
    }

    if (
      config.IS_WHITELABEL_APP &&
      !isWhitelabelDataFetched &&
      checkValidWhitelabelSubdomain(subdomain)
    ) {
      const partner_id = urlParams.get("partner_id");
      dispatch(whitelabelMiddlewareClient(subdomain, partner_id || "", hostname, port, domainUrl));
    }
    if (typeof window !== "undefined" && isWhitelabel) {
      window.addEventListener("message", (event) => {
        if (typeof event.data === "string" && event.data.includes("amberwhitelabelconfig")) {
          const data = JSON.parse(event.data);
          const amberwhitelabelconfig = data?.amberwhitelabelconfig;
          document.documentElement.style.setProperty(
            "--whitelabel_primary_color",
            `${amberwhitelabelconfig?.brand_color}`,
          );
          document.documentElement.style.setProperty(
            "--whitelabel_primary_text_color",
            `${getFontColorForBackground(amberwhitelabelconfig?.brand_color)}`,
          );
          document.documentElement.style.setProperty(
            "--whitelabel_cta_color",
            `${amberwhitelabelconfig?.listing?.cta_color}`,
          );
          const cta_text_color = getFontColorForBackground(
            amberwhitelabelconfig?.listing?.cta_color,
          );
          document.documentElement.style.setProperty(
            "--whitelabel_cta_text_color",
            `${cta_text_color}`,
          );
          document.documentElement.style.setProperty(
            "--whitelabel_footer_background_color",
            `${amberwhitelabelconfig?.footer?.bg_color}`,
          );
          document.documentElement.style.setProperty(
            "--whitelabel_footer_text_color",
            `${getFontColorForBackground(amberwhitelabelconfig?.footer?.bg_color)}`,
          );
          const fontPref = amberwhitelabelconfig?.font_preference;
          if (fontPref) {
            // Load fonts dynamically for real-time updates
            const fontUrls = buildGoogleFontUrls(fontPref);

            // Add new font links if they don't exist
            fontUrls.forEach((url) => {
              const existingLink = document.querySelector(`link[href="${url}"]`);
              if (!existingLink) {
                const link = document.createElement("link");
                link.rel = "stylesheet";
                link.href = url;
                document.head.appendChild(link);
              }
            });

            // Set CSS variables
            if (fontPref.mode === "single" && fontPref.single_font) {
              document.documentElement.style.setProperty(
                "--whitelabel_title_font",
                fontPref.single_font,
              );
              document.documentElement.style.setProperty(
                "--whitelabel_body_font",
                fontPref.single_font,
              );
            } else if (fontPref.mode === "pairing") {
              document.documentElement.style.setProperty(
                "--whitelabel_title_font",
                fontPref.title_font || "inherit",
              );
              document.documentElement.style.setProperty(
                "--whitelabel_body_font",
                fontPref.body_font || "inherit",
              );
            }
          }
          data.config = amberwhitelabelconfig;
          dispatch(getWhitelabelDataSuccess(data));
        }
      });
    }

    const containsFooter = urlParams.get("white_label_footer");
    if (containsFooter) {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }, []);

  // useEffect for route change
  useEffect(() => {
    // in-app routing
    if (tolgeeRouteData?.route?.tagforkey?.length) {
      const routeTags = tolgeeRouteData.route.tagforkey;
      if (isUsingS3Fallback) {
        return;
      }

      const missingTags = routeTags.filter((tag: string) => !loadedRouteTags?.includes(tag));

      if (missingTags.length) {
        dispatch(
          translationClientMiddleware({
            lang: userLanguage || "en",
            tags: missingTags,
          }),
        );
      }
    }
  }, [location.pathname, userLanguage, isUsingS3Fallback]);

  useEffect(() => {
    if (tolgeeRouteData?.route?.loadClarity) {
      dispatch(updateLoadClarity(true));
    }
    if (tolgeeRouteData?.route?.prefetch) {
      setTimeout(() => {
        tolgeeRouteData?.route?.prefetch?.forEach((it: any) => {
          it.preload();
        });
      }, 2000);
    }

    if (tolgeeRouteData?.route?.moduleName !== "SearchPage") {
      window.removeLazyMapLoad = true;
    }
  }, [location.pathname]); // Run when pathname changes

  useEffect(() => {
    if (config?.IS_CHINA_APP) {
      hideUnreadMessagesIframe();
      if (isDesktop) addBottomPaddingtoMessagingWindow();
      hideDefaultLauncherButtonIframe();
    }

    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      if (code) {
        sendAccessToken({
          authorization_code: code,
          type: "wechat",
          // nextRoute: Config.urls.web_service_url,
        });
      }
    }
  }, []);

  useEffect(() => {
    const routeData = matchRoutes(routes, window.location.pathname).reduce(
      (res: any, { route, match }: any) => {
        if (match.isExact) {
          return { route, match };
        }
        return res;
      },
      null,
    );
    if (
      Array.isArray(routeData?.route?.loadClarity)
        ? routeData?.route?.loadClarity.includes(countryCode)
        : !!routeData?.route?.loadClarity
    ) {
      dispatch(updateLoadClarity(true));
    }
  }, [JSON.stringify(countryCode)]);

  useEffect(() => {
    if (isMiniProgram) {
      const params = new URLSearchParams(window?.location?.search);
      localStorage.setItem("clientId", params.get("clientId") || "");
      localStorage.setItem("sessionId", params.get("sessionId") || "");
    }
    const internalUser = isInternalUser(userData);

    // Get route data for web vitals
    const routeData = matchRoutes(routes, window.location.pathname).reduce(
      (res: any, { route, match }: any) => {
        if (match.isExact) {
          return { route, match };
        }
        return res;
      },
      null,
    );
    // 🔤 CLIENT-SIDE TOLGEE FETCH (IN-APP ROUTING)
    if (routeData?.route?.tagforkey?.length) {
      const routeTags = routeData.route.tagforkey;

      const missingTags = routeTags.filter(
        (tag: string) => !loadedRouteTags?.includes(tag), // Fixed: was loadedTolgeeTags
      );

      if (missingTags.length) {
        dispatch(
          translationClientMiddleware({
            lang: userLanguage,
            tags: missingTags,
          }),
        );
      }
    }

    // scroll reset on page refresh
    function closeIt() {
      if (!(isDesktop && internalUser)) {
        window.scrollTo(0, 0);
      }
    }
    window.onbeforeunload = closeIt;

    if (routeData?.route?.prefetch) {
      setTimeout(() => {
        routeData?.route?.prefetch?.forEach((it: any) => {
          it.preload();
        });
      }, 2000);
    }

    if (routeData?.route?.moduleName !== "SearchPage") {
      window.removeLazyMapLoad = true;
    }

    const sendToServer = async (metric: any) => {
      const profiling = (window as any).__SERVER_PROFILING__;
      const enrichedMetric = {
        id: metric.id,
        name: metric.name,
        value: metric.value,
        delta: metric.delta,
        rating: metric.rating,
        navigationType: metric.navigationType,
        routePattern: routeData?.route.path,
        moduleName: routeData?.route?.moduleName,
        pathname: window.location.pathname,
        href: window.location.href,
        deviceType: getDeviceType(),
        viewportSize: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        isUserLoggedIn,
        entries: metric.entries,
        attribution: metric.attribution,
        origin: window?.location?.origin,
        country: country || localStorage.getItem("country"),
        internalUser,
        connectionType: (navigator as any).connection?.effectiveType,
        serverProfiling: profiling
          ? {
              dataResponseTime: profiling.dataResponseTime,
              appRendringTime: profiling.appRendringTime,
            }
          : undefined,
        webVitalsVersion: 5,
        ...(window?.isApp && {
          isApp: window?.isApp,
          appVersion: window?.appVersion || "",
        }),
      };

      try {
        await fetch("/api/log-web-vitals", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(enrichedMetric),
        });
      } catch (err) {
        console.error("Failed to send web vitals:", err);
      }
    };

    const sendData = (data: any) => {
      sendToServer(data);
      /** try {
        if (data) {
          const webVitalsData = {
            name: data.name,
            value: data.value || 0,
            page: routeData?.route.path,
            path_name: window.location.pathname,
            rating: data.rating,
            delta: Math.round(data.name === "CLS" ? data.delta * 1000 : data.delta),
            id: data.id,
            device: isDesktop ? "desktop" : "mobile",
            user_agent: window.navigator.userAgent,
            entries: data?.entries,
            attribution: data?.attribution,
            is_internal_user: internalUser,
            is_loggedin: isUserLoggedIn,
            isv2: true,
            api_response_time: window.__SERVER_PROFILING__?.dataResponseTime,
            app_rendering_time: window.__SERVER_PROFILING__?.appRendringTime,
            server_latency:
              window.__SERVER_PROFILING__?.dataResponseTime +
              window.__SERVER_PROFILING__?.appRendringTime,
          };
          axios.post(`${config.COMMUNICATION_SERVICE_URL}/api/web_vitals`, { ...webVitalsData });
        }
      } catch {
       } * */
    };

    onINP(sendData);
    onLCP(sendData);
    onTTFB(sendData);
    onCLS(sendData);
  }, []);

  return (
    <>
      {__CLIENT__ &&
        !isWhitelabel &&
        (!isAuthTokenPresent || isUserDataFetched) &&
        !config.IS_CHINA_APP &&
        !isAmberApp &&
        !isContentPage && (
          <SnapchatPixelScript
            isUserLoggedIn={isUserLoggedIn}
            userData={userData}
            nonce={
              // @ts-ignore
              (window as any)?.__INITIAL_STATE__?.appData?.nonce || undefined
            }
          />
        )}
      {__CLIENT__ && loadClarity && !isContentPage && (
        <ClarityScript
          nonce={
            // @ts-ignore
            (window as any)?.__INITIAL_STATE__?.appData?.nonce || undefined
          }
        />
      )}
      {isAmberApp && <AppLayout />}
    </>
  );
};
export default wrapperHOC(UserDataLayout, {
  componentName: "UserDataLayout",
  showForChina: true,
});
